// Package imports:
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
// Component imports:
import Ad, { IAdProps } from './Ad';
// Service imports:
import { useRefreshRateFetching } from '../../services/hooks';
import { CUSTOM_REFRESH_RATES } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { Fetched } from '../../types/Types';

type AdLocation = 'Company310x400' | 'FrontPage310x400A' | 'FrontPage310x400B' | 'FrontPage310x400C' | 'FrontPage310x400D'
    | 'FrontPage400x80A' | 'FrontPage400x80B'
    | 'Head1360x180' | 'Head400x80' | 'Market1018x360' | 'Market400x80' | 'Market310x400' | 'MarketHead1360x180' | 'MarketHead400x80' | 'MarketHead1018x360';

interface IProps {
    location: AdLocation,
    duration?: number
}
const adSizes = {
    'Company310x400': {
        width: 310,
        height: 400
    },
    'Company900x400': {
        width: 900,
        height: 400
    },
    'FrontPage310x400A': {
        width: 310,
        height: 400
    },
    'FrontPage310x400B': {
        width: 310,
        height: 400
    },
    'FrontPage310x400C': {
        width: 310,
        height: 400
    },
    'FrontPage310x400D': {
        width: 310,
        height: 400
    },
    'FrontPage400x80A': {
        width: 400,
        height: 80
    },
    'FrontPage400x80B': {
        width: 400,
        height: 80
    },
    'Head1360x180': {
        width: 1360,
        height: 180
    },
    'Head400x80': {
        width: 400,
        height: 80
    },
    'Market1018x360': {
        width: 1018,
        height: 360
    },
    'Market400x80': {
        width: 400,
        height: 80
    },
    'Market310x400': {
        width: 310,
        height: 400
    },
    'MarketHead1360x180': {
        width: 1360,
        height: 180
    },
    'MarketHead400x80': {
        width: 400,
        height: 80
    },
    'MarketHead1018x360': {
        width: 1360,
        height: 360
    }
};

const AdRotator: React.FC<IProps> = ({
    location,
    duration = 120000
}) => {
    const [width, height] = [adSizes[location].width, adSizes[location].height];
    const [ads, setAds] = useState<Fetched<IAdProps[]>>(null);
    const [adIndex, setAdIndex] = useState(0);
    const [nextInCat, setNextInCat] = useState<{[T in string]: number}>({});
    const increaseAdIndex = () => {
        if (ads === null || ads instanceof Error) return;
        if (ads[adIndex]?.metadata.cat) {
            const nextIndexInCat = adIndex < ads.length - 1 && ads[adIndex+1].metadata.cat && ads[adIndex].metadata.cat === ads[adIndex+1].metadata.cat
                ? adIndex + 1
                : ads.findIndex((ad) => ad.metadata.cat && ad.metadata.cat === ads[adIndex].metadata.cat);
            setNextInCat({
                ...nextInCat,
                [ads[adIndex].metadata.cat ?? '']: nextIndexInCat
            });
        }
        let newIndex = (adIndex + 1) % ads.length;
        while (ads[adIndex]?.metadata.cat && ads[newIndex].metadata.cat && ads[adIndex]?.metadata.cat === ads[newIndex].metadata.cat) {
            newIndex = (newIndex + 1) % ads.length;
        }
        const newCat = ads[newIndex].metadata.cat;
        if (newCat && nextInCat[newCat] !== undefined && newIndex !== nextInCat[newCat]) {
            newIndex = nextInCat[newCat];
        }
        setAdIndex(newIndex);
    }

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            increaseAdIndex();
        }, duration);
        return () => window.clearInterval(intervalId);
    }, [ ads, increaseAdIndex ]);
    
    const fetchData = async () => {
        setNextInCat({});
        setAdIndex(0);
        if (location === 'MarketHead1018x360') {
            try {
                const url1 = `https://ads.livemarketdata.com/v1/ads/MarketHead1360x180`;
                const response1 = await fetch(url1);
                let body1: IAdProps[] = [];
                if (response1.ok) {
                    body1 = await response1.json();
                }
                const url2 = `https://ads.livemarketdata.com/v1/ads/MarketHead1018x360`;
                const response2 = await fetch(url2);
                let body2: IAdProps[] = [];
                if (response2.ok) {
                    body2 = await response2.json();
                }
                if (body1.length > 0 || body2.length > 0) {
                    for (let i = 0; i < body1.length; i++) {
                        body1[i].height = 180;
                        body1[i].width = 1360;
                    }
                    for (let i = 0; i < body2.length; i++) {
                        body2[i].height = 360;
                        body2[i].width = 1018;
                    }
                    setAds(handleData([...body1, ...body2]));
                } else {
                    setAds(new Error(ErrorMessages.RequestFailed));
                }
            } catch (e) {
                setAds(new Error(ErrorMessages.NetworkError));
            }
        }
        else {
            try {
                const url = `https://ads.livemarketdata.com/v1/ads/${location}`;
                const response = await fetch(url);
                if (response.ok) {
                    const body = await response.json();
                    setAds(handleData(body as IAdProps[]));
                } else {
                    setAds(new Error(ErrorMessages.RequestFailed));
                }
            } catch (e) {
                
                setAds(new Error(ErrorMessages.NetworkError));
            }
        }
    }
    const handleData = (ads: IAdProps[]) => {
        if (ads === null || ads instanceof Error) {
            return [];
        } else {
            const filteredAds = ads.filter((ad) => (
                !ad.metadata.expired
                && (!ad.metadata.expires || Date.parse(ad.metadata.expires) > Date.now())
                && (!ad.metadata.show || Date.parse(ad.metadata.show) < Date.now())
            ));
            const shuffledAds = shuffle(filteredAds);
            return shuffledAds;
        }
    }

    const shuffle = (adsList: IAdProps[]) => {
        const sorted = adsList.sort((a, b) => {
            return (a.metadata.cat === null || a.metadata.cat === undefined) && (b.metadata.cat === null || b.metadata.cat === undefined)
            ? 0
            : a.metadata.cat === null || a.metadata.cat === undefined
            ? 1
            : b.metadata.cat === null || b.metadata.cat === undefined
            ? -1
            : a.metadata.cat > b.metadata.cat
            ? 1
            : a.metadata.cat < b.metadata.cat
            ? -1
            : 0
        });
        
        let cats: {index: number, n: number}[] = [];
        for (let i = 0; i < sorted.length;) {
            let n = 1;
            while (i+n < sorted.length && sorted[i].metadata.cat && sorted[i+n].metadata.cat && sorted[i+n].metadata.cat === sorted[i].metadata.cat) {
                n++;
            }
            cats.push({
                index: i,
                n: n
            });
            i += n;
        }
        let currentIndex = cats.length, randomIndex;
        // While there remain elements to shuffle...
        while (currentIndex !== 0) {

            // Pick a remaining element...
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;
            // And swap it with the current element.
            [cats[currentIndex], cats[randomIndex]] = [
            cats[randomIndex], cats[currentIndex]];
        }

        let newAdsList: IAdProps[] = [];
        cats.forEach(({index, n}) => {
            if (n > 1) {
                let currentIndex = index + n, randomIndex;

                // While there remain elements to shuffle...
                while (currentIndex !== index) { 
                    // Pick a remaining element...
                    randomIndex = index + Math.floor(Math.random() * n);
                    currentIndex--;
                    // And swap it with the current element.
                    [sorted[currentIndex], sorted[randomIndex]] = [
                    sorted[randomIndex], sorted[currentIndex]];
                }
            }
            newAdsList = newAdsList.concat(sorted.slice(index, index + n));
        })

        return newAdsList;
    }
    useRefreshRateFetching(fetchData, CUSTOM_REFRESH_RATES['ADS'], undefined, undefined, undefined, true);

    return (
        <div className={cx('KCL_ad-rotator', `ads${width}x${height}`)} id={`Ads_${location}`}>
            <div className="inner">
                {
                    ads !== null && !(ads instanceof Error) && ads.length > adIndex
                    ? <Ad
                        path={ads[adIndex].path}
                        metadata={ads[adIndex].metadata}
                        height={ads[adIndex].height ?? height}
                        width={ads[adIndex].width ?? width}
                        id={`Ad_${location}`}
                    />
                    : null
                }
            </div>
        </div>
    );
}

export default AdRotator;